// TabContent1.js
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { tokens } from '../../../../../theme';
import { useDispatch, useSelector } from 'react-redux';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import Shbutton from '../../../../theme/buttons/Shbutton';
import { config } from '../../../../../newapi/apiUrlConfig';

// The only accounting software without a monthly fee; every other type has to be
// accepted by the user before the flow can continue.
const FREE_ACCOUNTINGSOFTWARE_TYPE = "stockhub_pdf";

// Exact Online regional endpoints the user can target.
const EXACT_REGION_OPTIONS = [
    { value: 'nl', label: 'Nederland (nl)' },
    { value: 'be', label: 'België (be)' },
    { value: 'uk', label: 'United Kingdom (uk)' },
    { value: 'de', label: 'Deutschland (de)' },
];

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const [accountingSoftwareCredentials, setAccountingSoftwareCredentials] = useState(data.credentials);

    const handleDetailsChange = (key, val) => {
        setAccountingSoftwareCredentials({
            ...accountingSoftwareCredentials,
            [key]: val
        });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    }

    useEffect(() => {
        props.handleDataUpdate({ credentials: accountingSoftwareCredentials });
    }, [accountingSoftwareCredentials]);

    return (
        <>
            {data.type !== FREE_ACCOUNTINGSOFTWARE_TYPE &&
                <MonthlyCostsAcceptance
                    theme={theme}
                    colors={colors}
                    accepted={data.acceptMonthlyCosts === true}
                    onChange={(accepted) => props.handleDataUpdate({ acceptMonthlyCosts: accepted })}
                />
            }
            <Box 
                sx={{
                    width:1,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    gap:7
                }}
            >
                
                <Box
                    sx={{
                        width:120,
                    }}
                >
                    <Text>{t("description")}</Text>
                </Box>
                
                <Box
                    sx={{
                        flex:1,
                        backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                        borderRadius:4,
                        display:"flex"
                    }}
                >
                    <ShValidatedInput
                        name="description"
                        value={data.description ?? ""}
                        onChange={handleInput}
                        error={props.errors.description}
                    />
                    
                </Box>
            </Box>
            {data.type === "exact_online" ? (
                <ExactCredentials
                    theme={theme}
                    colors={colors}
                    credentials={accountingSoftwareCredentials}
                    onChange={handleDetailsChange}
                    errors={props.errors}
                />
            ) : (
                Object.keys(accountingSoftwareCredentials)
                .map((key) => (
                    <Box
                        key={key}
                        sx={{
                            width: 1,
                            paddingTop: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 7
                        }}
                    >
                        <Box
                            sx={{
                                width: 120,
                            }}
                        >
                            <Text>{t(key)}</Text>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                //backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                                borderRadius: 4,
                                display: "flex"
                            }}
                        >
                            <ShValidatedInput
                                name={key}
                                value={accountingSoftwareCredentials[key] ?? ""}
                                onChange={handleDetailsChange}
                                error={props.errors[`credentials.${key}`]}
                            />
                        </Box>
                    </Box>
                ))
            )}
    </>
    );
};

/**
 * MonthlyCostsAcceptance
 *
 * Warning plus acceptance checkbox for the paid accounting software types. The Details tab
 * keeps the next button disabled until the checkbox is ticked.
 */
const MonthlyCostsAcceptance = ({ theme, colors, accepted, onChange }) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                width: 1,
                marginBottom: 2,
                padding: 2,
                borderRadius: 4,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.orange[100],
                border: `1px solid ${colors.orange[400]}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <WarningAmberOutlinedIcon sx={{ color: colors.orange[400] }} />
                <Text sx={{ color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.orange[400] }}>
                    {t("accountingsoftware_monthly_costs_warning")}
                </Text>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Checkbox
                    checked={accepted}
                    onChange={(e) => onChange(e.target.checked)}
                    size="small"
                    sx={{
                        color: colors.grey[300],
                        '&.Mui-checked': { color: colors.orange[400] },
                    }}
                />
                <Box onClick={() => onChange(!accepted)} sx={{ cursor: "pointer" }}>
                    <Text semibold>{t("accept_accountingsoftware_monthly_costs")}</Text>
                </Box>
            </Box>
        </Box>
    );
};

/**
 * ExactCredentials
 *
 * Region select, client id/secret inputs and a read-only callback URL. The Connect step
 * lives in the accounting-software sidepanel, since the record must be saved (and have an id)
 * before the OAuth exchange can run.
 */
const ExactCredentials = ({ theme, colors, credentials, onChange, errors }) => {
    const { t } = useTranslation();
    const callbackUrl = `${config.url.API_BACKEND_URL}oauth/exact/callback`;
    const inputBg = theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100];

    const rowSx = {
        width: 1,
        paddingTop: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 7,
    };

    const handleCopyCallback = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(callbackUrl);
        }
    };

    return (
        <>
            <Box sx={rowSx}>
                <Box sx={{ width: 120 }}>
                    <Text>{t("region")}</Text>
                </Box>
                <Box sx={{ flex: 1, backgroundColor: inputBg, borderRadius: 4, display: "flex" }}>
                    <InputDropdown
                        name="region"
                        displayName="label"
                        noSelection={t('choose_option')}
                        options={EXACT_REGION_OPTIONS}
                        selected={EXACT_REGION_OPTIONS.findIndex(o => o.value === (credentials?.region || 'nl'))}
                        onChange={(selected) => onChange('region', EXACT_REGION_OPTIONS[selected].value)}
                        closeOnSelection
                    />
                </Box>
            </Box>

            <Box sx={rowSx}>
                <Box sx={{ width: 120 }}>
                    <Text>{t("client_id")}</Text>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                    <ShValidatedInput
                        name="client_id"
                        value={credentials?.client_id ?? ""}
                        onChange={onChange}
                        error={errors["credentials.client_id"]}
                    />
                </Box>
            </Box>

            <Box sx={rowSx}>
                <Box sx={{ width: 120 }}>
                    <Text>{t("client_secret")}</Text>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                    <ShValidatedInput
                        name="client_secret"
                        type="password"
                        value={credentials?.client_secret ?? ""}
                        onChange={onChange}
                        error={errors["credentials.client_secret"]}
                    />
                </Box>
            </Box>

            <Box sx={rowSx}>
                <Box sx={{ width: 120 }}>
                    <Text>{t("callback_url")}</Text>
                </Box>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1, backgroundColor: inputBg, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInput
                            name="callback_url"
                            value={callbackUrl}
                            readOnly
                            onChange={() => {}}
                        />
                    </Box>
                    <Shbutton color="grey" variant="contained" onClick={handleCopyCallback}>
                        {t('copy')}
                    </Shbutton>
                </Box>
            </Box>
            <Box sx={{ width: 1, paddingTop: 1 }}>
                <Text light>{t("exact_callback_url_note")}</Text>
            </Box>
        </>
    );
};

const Details = (props) => {
    const { t } = useTranslation();
    // Every type except the free one has monthly costs, which the user has to accept
    // before the flow can continue.
    const tabData = useSelector((state) => state.progressiontabs.data);
    const requiresCostsAcceptance = tabData?.type !== FREE_ACCOUNTINGSOFTWARE_TYPE;

    // Create a mapping of fields to their webshop types
    const FIELD_TO_ACCOUNTINGSOFTWARE = {
        'administration_id': ['moneybird'],
        'access_token': ['moneybird'],
        'client_id': ['exact_online'],
        'client_secret': ['exact_online'],
        'region': ['exact_online'],
    };

    // Create conditional schema
    const conditionalSchema = [
        {
            key: "description",
            errorField: t('description'),
            alwaysRequired: true,
            type: "text",
        },
        ...Object.entries(FIELD_TO_ACCOUNTINGSOFTWARE).map(([field, accountingSoftwareTypes]) => ({
            key: `credentials.${field}`,  // Add credentials prefix
            errorField: t(field),
            alwaysRequired: false,
            condition: (data) => accountingSoftwareTypes.includes(data.type),
            type: "text"
        }))
    ];

    return (
        <ProgressionTab 
            tabId="tabDetails" 
            subtitle={t("details")} 
            step={2}
            maxSteps={3}
            nextTab={"tabSync"}
            conditionalSchema={conditionalSchema}
            nextDisabled={requiresCostsAcceptance && tabData?.acceptMonthlyCosts !== true}
        >
            <Content />
        </ProgressionTab>
    )
}

export default Details;